import {createSelector} from "@reduxjs/toolkit";
import {chain, memoize} from "lodash";
import {IStore, NullOrEmpty} from "modules/types";
import {getPlayerPoolFilters} from "modules/selectors";
import {
	filterByPosition,
	filterBySquad,
	sortByStats,
	filterPlayersBySearch,
	parseRequestState,
} from "modules/utils";
import {PlayerPositionEnum, VotingStatus} from "modules/enums";

const getState = ({jsonReducer}: IStore) => jsonReducer;

export const getPlayers = createSelector(getState, (state) => state.players);
export const getIsLoadingPlayers = createSelector(
	getState,
	({playersRequestState}) => !parseRequestState(playersRequestState).isComplete
);

export const getSquads = createSelector(getState, (state) => state.squads);
export const getIsLoadingSquads = createSelector(
	getState,
	({squadsRequestState}) => !parseRequestState(squadsRequestState).isComplete
);

export const getIsLoading = createSelector(
	getIsLoadingPlayers,
	getIsLoadingSquads,
	(isLoadingPlayers, isLoadingSquads) => isLoadingPlayers || isLoadingSquads
);

export const getCountries = createSelector(getState, (state) => state.countries);
export const getSettings = createSelector(getState, (state) => state.settings);
export const getIsVotingOpen = createSelector(
	getSettings,
	(settings) => settings.votingPeriodStatus === VotingStatus.Opened
);
export const getIsSettingsLoaded = createSelector(
	getState,
	(state) => parseRequestState(state.settingsRequestState).isComplete
);

export const getSortedPlayers = createSelector(
	getPlayers,
	getPlayerPoolFilters,
	(players, filters) => {
		return chain(players)
			.filter((player) => filterPlayersBySearch(player, filters.value))
			.filter((player) => filterByPosition(player, filters.position))
			.filter((player) => filterBySquad(player, filters.team))
			.orderBy(
				[(player) => sortByStats(player, filters.stats), (player) => player.lastName],
				["desc", "asc"]
			)
			.value();
	}
);

export const getPlayerById = createSelector(getPlayers, (players) => {
	return memoize((id: NullOrEmpty<number> = 0) => {
		return players.find((player) => player.id === id);
	});
});

export const getPlayerByFeedId = createSelector(getPlayers, (players) => {
	return memoize((id: NullOrEmpty<number> = 0, position: PlayerPositionEnum) => {
		return players.find((player) => player.feedId === id && player.position === position);
	});
});

export const getSquadById = createSelector(getSquads, (squads) => {
	return memoize((id: NullOrEmpty<number> = 0) => {
		return squads.find((squad) => squad.id === id);
	});
});

export const getFanVoteRankings = createSelector(getState, ({fanVoteRankings}) =>
	fanVoteRankings.filter(({percent}) => percent !== 0)
);

export const getArticles = createSelector(getState, ({articles}) => articles);
